import { Package, Star, Store } from "lucide-react";
import type { AdsRow, PlannerRow } from "../../types/planner";
import { Chip, ChipButton } from "../chip";

const nf = new Intl.NumberFormat("da-DK");
const price = (v: number | null) =>
	v == null ? "—" : `${nf.format(Math.round(v))} kr`;

const cssVariant = (
	row: PlannerRow,
): "success" | "warning" | "danger" | "slate" => {
	if (row.cssPolicyBlocked) return "danger";
	if (row.cssServingStatus === "approved") return "success";
	if (row.cssServingStatus === "pending") return "warning";
	return "slate";
};

const generatedBadge = (row: PlannerRow) => {
	if (row.generatedContent)
		return { label: "Generated", variant: "success" as const };
	if (row.titleOptimized)
		return { label: "Title only", variant: "warning" as const };
	return { label: "Not generated", variant: "slate" as const };
};

interface AttributeChipsProps {
	row: PlannerRow;
	onAttributeClick?: (field: string, value: string) => void;
}

const AttributeChips = ({ row, onAttributeClick }: AttributeChipsProps) => {
	const attrs: { field: string; value: string | null }[] = [
		{ field: "brand", value: row.brand },
		{ field: "color", value: row.color },
		{ field: "size", value: row.size },
		{ field: "gender", value: row.gender },
	];
	const present = attrs.filter(
		(a): a is { field: string; value: string } => !!a.value,
	);
	if (present.length === 0) return <span className="text-slate-300">—</span>;
	return (
		<div className="flex flex-wrap gap-1">
			{present.map((a) =>
				onAttributeClick ? (
					<ChipButton
						key={a.field}
						variant="slate"
						size="xs"
						onClick={() => onAttributeClick(a.field, a.value)}
						title={`Add ${a.field} = ${a.value} as a filter`}
					>
						{a.value}
					</ChipButton>
				) : (
					<Chip key={a.field} variant="slate" size="xs">
						{a.value}
					</Chip>
				),
			)}
		</div>
	);
};

interface ProductRowProps {
	row: PlannerRow | AdsRow;
	onAttributeClick?: (field: string, value: string) => void;
	showConversions?: boolean;
}

/**
 * The row shape shared by every Planner tab (§1.5) — image, name, merchants,
 * price range + spread, a Generated chip, rating, clickable attribute chips,
 * CSS status and traffic. Tab 2 adds a Conversions column via `showConversions`.
 */
export const ProductRow = ({
	row,
	onAttributeClick,
	showConversions,
}: ProductRowProps) => {
	const generated = generatedBadge(row);
	const ads = "conversions" in row ? (row as AdsRow) : null;

	return (
		<tr className="border-t border-slate-100">
			<td className="px-4 py-3">
				<div className="flex items-center gap-3">
					<div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-50">
						{row.imageUrl ? (
							<img
								src={row.imageUrl}
								alt={row.name}
								className="h-full w-full object-contain"
							/>
						) : (
							<Package className="h-4 w-4 text-slate-300" />
						)}
					</div>
					<div className="min-w-0">
						<a
							href={`/products/${row.productId}`}
							className="line-clamp-2 text-sm font-medium text-slate-700 hover:text-primary"
						>
							{row.name}
						</a>
						<p className="text-xs text-slate-400">#{row.productId}</p>
					</div>
				</div>
			</td>
			<td className="px-4 py-3 tabular-nums text-slate-600">
				<span className="flex items-center gap-1">
					<Store className="h-3.5 w-3.5 text-slate-300" />
					{row.merchantCount}
				</span>
			</td>
			<td className="px-4 py-3 tabular-nums text-slate-700">
				{row.minPrice != null &&
				row.maxPrice != null &&
				row.minPrice !== row.maxPrice
					? `${price(row.minPrice)} – ${price(row.maxPrice)}`
					: price(row.minPrice ?? row.maxPrice)}
				{row.spreadPct != null && row.spreadPct > 0 && (
					<span className="ml-1.5 text-xs text-slate-400">
						+{row.spreadPct.toFixed(0)}%
					</span>
				)}
			</td>
			<td className="px-4 py-3">
				<Chip variant={generated.variant} size="xs">
					{generated.label}
				</Chip>
			</td>
			<td className="px-4 py-3 tabular-nums text-slate-600">
				{row.reviewRating != null ? (
					<span className="flex items-center gap-1">
						<Star className="h-3.5 w-3.5 fill-warning text-warning" />
						{row.reviewRating.toFixed(1)}
						<span className="text-xs text-slate-400">
							({nf.format(row.reviewCount)})
						</span>
					</span>
				) : (
					<span className="text-slate-300">—</span>
				)}
			</td>
			<td className="px-4 py-3">
				<AttributeChips row={row} onAttributeClick={onAttributeClick} />
			</td>
			<td className="px-4 py-3">
				<Chip variant={cssVariant(row)} size="xs">
					{row.cssPolicyBlocked
						? "blocked"
						: (row.cssServingStatus ?? "unknown")}
				</Chip>
			</td>
			<td className="px-4 py-3 tabular-nums text-slate-500">
				{nf.format(row.gscImpressions30d)}
				{row.gscAvgPosition30d != null && (
					<span className="ml-1.5 text-xs text-slate-400">
						@{row.gscAvgPosition30d.toFixed(1)}
					</span>
				)}
			</td>
			{showConversions && (
				<td className="px-4 py-3 tabular-nums text-slate-700">
					{ads
						? ads.conversions > 0
							? nf.format(ads.conversions)
							: ads.merchantConversions != null
								? `~${nf.format(ads.merchantConversions)}`
								: "—"
						: "—"}
				</td>
			)}
		</tr>
	);
};
